
import React from 'react';
import { useNotifications, Notification } from '@/contexts/NotificationsContext';
import { Button } from '@/components/ui/button';
import { CheckCheck, Diamond, CheckCircle } from 'lucide-react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { sanitizeExistingUrl } from '@/utils/url-validation';
import { DiscordLinkBanner } from './DiscordLinkBanner';

export const NotificationsList: React.FC = () => {
  const { notifications, loading, unreadCount, markAllAsRead, markAsRead, setShowNotifications } = useNotifications();

  const renderNotificationContent = (notification: Notification) => {
    const username = notification.actor_username || 'Someone';
    
    switch (notification.type) {
      case 'like':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> liked your bulletin
            {notification.is_deleted && <span className="text-gray-400 italic"> (content no longer available)</span>}
          </span>
        );
      case 'follow':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> followed you
          </span>
        );
      case 'comment':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> commented on your {notification.loop_id ? 'loop' : 'bulletin'}
            {notification.is_deleted && <span className="text-gray-400 italic"> (content no longer available)</span>}
          </span>
        );
      case 'mention':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> mentioned you
            {notification.is_deleted && <span className="text-gray-400 italic"> (content no longer available)</span>}
          </span>
        );
      case 'relic':
        return (
          <span className="font-semibold text-amber-600 dark:text-amber-400">
            Your bulletin has become a relic! 💎
          </span>
        );
      case 'verified':
        return (
          <span className="font-semibold text-green-600 dark:text-green-400">
            🎉 You're now verified human! Welcome to FOMO! ✨
          </span>
        );
      case 'save':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> saved your post to a moodboard
            {notification.is_deleted && <span className="text-gray-400 italic"> (content no longer available)</span>}
          </span>
        );
      case 'reloop':
        return (
          <span>
            <Link 
              to={`/profile/${username}`} 
              className="font-semibold hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              @{username}
            </Link> relooped your {notification.loop_id ? 'loop' : 'post'}
            {notification.is_deleted && <span className="text-gray-400 italic"> (content no longer available)</span>}
          </span>
        );
      default:
        return <span>You have a new notification</span>;
    }
  };

  const getNotificationLink = (notification: Notification) => {
    // For deleted content, don't provide a link
    if (notification.is_deleted && (notification.type === 'like' || notification.type === 'comment' || notification.type === 'mention' || notification.type === 'save' || notification.type === 'reloop')) {
      return '#';
    }
    
    switch (notification.type) {
      case 'like':
      case 'comment':
      case 'save':
      case 'reloop':
        if (notification.loop_id) {
          return `/loops?view=feed&post=${notification.loop_id}`;
        }
        return notification.bulletin_id ? `/bulletins/${notification.bulletin_id}` : '#';
      case 'follow':
        return notification.actor_id ? `/profile/${notification.actor_username}` : '#';
      case 'mention':
        if (notification.loop_id) {
          return `/loops?view=feed&post=${notification.loop_id}`;
        }
        return notification.bulletin_id ? `/bulletins/${notification.bulletin_id}` : '#';
      case 'relic':
        return notification.bulletin_id ? `/bulletins/${notification.bulletin_id}` : '#';
      case 'verified':
        return '#'; // No specific link for verification notification
      default:
        return '#';
    }
  };

  const getAvatarUrl = (notification: Notification) => {
    try {
      if (!notification.actor_profile_image) return '';
      return sanitizeExistingUrl(notification.actor_profile_image);
    } catch (e) {
      console.error('Error processing avatar URL:', e);
      return '';
    }
  };

  if (loading) {
    return (
      <div className="p-4">
        <div className="flex flex-col space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-4">
              <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div>
        <DiscordLinkBanner onNavigate={() => setShowNotifications(false)} />
        <div className="p-8 text-center">
          <p className="text-gray-500">No notifications yet</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <DiscordLinkBanner onNavigate={() => setShowNotifications(false)} />
      {unreadCount > 0 && (
        <div className="p-2 border-b flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={async () => {
              await markAllAsRead();
              // Auto-close the notification panel once all are read
              setShowNotifications(false);
            }}
          >
            <CheckCheck className="h-3 w-3 mr-1" />
            Mark all as read
          </Button>
        </div>
      )}
      <div className="max-h-[70vh] overflow-y-auto">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`p-4 border-b hover:bg-muted/50 transition-colors ${
              !notification.read_at ? 'bg-muted/30' : ''
            } ${
              notification.type === 'relic' 
                ? 'bg-gradient-to-r from-amber-50/50 to-yellow-50/50 dark:from-amber-950/20 dark:to-yellow-950/20 border-l-4 border-amber-400' 
                : notification.type === 'verified'
                ? 'bg-gradient-to-r from-green-50/50 to-emerald-50/50 dark:from-green-950/20 dark:to-emerald-950/20 border-l-4 border-green-400'
                : ''
            }`}
          >
            {notification.is_deleted && (notification.type === 'like' || notification.type === 'comment' || notification.type === 'mention' || notification.type === 'relic' || notification.type === 'save' || notification.type === 'reloop') ? (
              // Don't wrap deleted content notifications in Links
              <div className="flex items-start space-x-4">
                <Link 
                  to={`/profile/${notification.actor_username || ''}`} 
                  className="flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Avatar className="h-10 w-10 rounded-full overflow-hidden">
                    <AvatarImage src={getAvatarUrl(notification)} alt={notification.actor_username || 'User'} className="object-cover w-full h-full" />
                    <AvatarFallback>{(notification.actor_username || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm">{renderNotificationContent(notification)}</p>
                    {notification.type === 'relic' && (
                      <Diamond className="h-4 w-4 text-amber-500 animate-pulse" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {notification.created_at
                      ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                      : 'Recently'}
                  </p>
                </div>
                {notification.image_url && (
                  <div className="flex-shrink-0">
                    <img 
                      src={notification.image_url} 
                      alt="Content preview" 
                      className="w-12 h-12 rounded object-cover"
                    />
                  </div>
                )}
              </div>
            ) : (
              <Link 
                to={getNotificationLink(notification)} 
                className="flex items-start space-x-4"
                onClick={(e) => {
                  console.log('Link clicked for notification:', notification.id, 'Read at:', notification.read_at);
                  if (!notification.read_at) {
                    markAsRead(notification.id);
                  }
                  // Close the notification panel after clicking
                  setShowNotifications(false);
                }}
              >
                <Link 
                  to={`/profile/${notification.actor_username || ''}`} 
                  className="flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Avatar className="h-10 w-10 rounded-full overflow-hidden">
                    <AvatarImage src={getAvatarUrl(notification)} alt={notification.actor_username || 'User'} className="object-cover w-full h-full" />
                    <AvatarFallback>{(notification.actor_username || 'U').charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm">{renderNotificationContent(notification)}</p>
                    {notification.type === 'relic' && (
                      <Diamond className="h-4 w-4 text-amber-500 animate-pulse" />
                    )}
                    {notification.type === 'verified' && (
                      <CheckCircle className="h-4 w-4 text-green-500 animate-pulse" />
                    )}
                  </div>
                  <p className="text-xs text-gray-500">
                    {notification.created_at
                      ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })
                      : 'Recently'}
                  </p>
                </div>
                {notification.image_url && (
                  <div className="flex-shrink-0">
                    <img 
                      src={notification.image_url} 
                      alt="Content preview" 
                      className="w-12 h-12 rounded object-cover"
                    />
                  </div>
                )}
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
